import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react'
import { createLocalAdapterSuite, getBrowserStorage, systemClock, systemIds } from '../adapters'
import { AppError, isAppError, messageForError } from '../domain/errors'
import {
  cleanText,
  failure,
  formatUsername,
  isValidEmail,
  isValidUsername,
  normalizeUsername,
  profileUsername,
  success,
} from '../domain/helpers'
import type {
  ActionResult,
  AppState,
  FeedChannel,
  Preferences,
} from '../domain/models'
import type { ClockPort, IdPort, LocalAdapterSuite, StoragePort } from '../domain/ports'
import type { AppActions, AppContextValue } from './contracts'
import { createInitialAppState } from './initialState'
import {
  clearPersistedSession,
  deletePersistedAccount,
  loadAccountState,
  loadPersistedAppState,
  resetPersistedAccountData,
  savePersistedAppState,
} from './persistence'
import { appReducer, type AppReducerAction } from './reducer'

const AppStateContext = createContext<AppState | null>(null)
const AppActionsContext = createContext<AppActions | null>(null)

export interface AppStateProviderProps extends PropsWithChildren {
  storage?: StoragePort
  adapters?: LocalAdapterSuite
  clock?: ClockPort
  ids?: IdPort
  initialOnline?: boolean
}

const expectedErrorStatus = (error: AppError): AppState['adapterStatuses']['auth'] => {
  if (error.status === 'unavailable' || error.status === 'offline') return error.status
  return 'ready'
}

const resultFromError = (error: unknown, fallbackCode: string, fallbackMessage: string): ActionResult => {
  if (isAppError(error)) return failure(error.code, error.message)
  return failure(fallbackCode, messageForError(error, fallbackMessage))
}

export function AppStateProvider({
  children,
  storage,
  adapters,
  clock = systemClock,
  ids = systemIds,
  initialOnline = typeof navigator === 'undefined' ? true : navigator.onLine,
}: AppStateProviderProps) {
  const storageRef = useRef<StoragePort>(storage ?? getBrowserStorage())
  const adaptersRef = useRef<LocalAdapterSuite>(adapters ?? createLocalAdapterSuite(storageRef.current))
  const clockRef = useRef<ClockPort>(clock)
  const idsRef = useRef<IdPort>(ids)
  const initialStateRef = useRef<AppState | null>(null)
  if (!initialStateRef.current) {
    initialStateRef.current = loadPersistedAppState(
      storageRef.current,
      initialOnline,
      clockRef.current.now(),
    )
  }
  const [state, dispatch] = useReducer(appReducer, initialStateRef.current)
  const stateRef = useRef(state)
  stateRef.current = state

  const commit = useCallback((action: AppReducerAction) => {
    stateRef.current = appReducer(stateRef.current, action)
    dispatch(action)
  }, [])

  useEffect(() => {
    savePersistedAppState(storageRef.current, state)
  }, [state])

  useEffect(() => {
    const updateOnline = () => {
      const online = navigator.onLine
      adaptersRef.current.setOnline(online)
      commit({ type: 'online-changed', online })
    }
    if (typeof window === 'undefined') return undefined
    window.addEventListener('online', updateOnline)
    window.addEventListener('offline', updateOnline)
    return () => {
      window.removeEventListener('online', updateOnline)
      window.removeEventListener('offline', updateOnline)
    }
  }, [commit])

  const actions = useMemo<AppActions>(() => ({
    async register(input) {
      commit({ type: 'set-adapter-status', adapter: 'auth', status: 'loading' })
      try {
        const account = await adaptersRef.current.auth.register(input)
        savePersistedAppState(storageRef.current, stateRef.current)
        const next = loadAccountState(
          storageRef.current,
          account.user,
          account.session,
          stateRef.current.online,
          true,
        )
        commit({ type: 'replace-state', state: next })
        return success('account_created', 'Account created. Confirm your email to finish setup.')
      } catch (error) {
        commit({
          type: 'set-adapter-status',
          adapter: 'auth',
          status: isAppError(error) ? expectedErrorStatus(error) : 'error',
        })
        return resultFromError(error, 'auth_unavailable', 'Account creation is unavailable right now.')
      }
    },

    async login(input) {
      commit({ type: 'set-adapter-status', adapter: 'auth', status: 'loading' })
      try {
        const account = await adaptersRef.current.auth.login(input)
        savePersistedAppState(storageRef.current, stateRef.current)
        const next = loadAccountState(
          storageRef.current,
          account.user,
          account.session,
          stateRef.current.online,
        )
        commit({ type: 'replace-state', state: next })
        return success('signed_in', `Welcome back, ${formatUsername(profileUsername(account.user))}.`)
      } catch (error) {
        commit({
          type: 'set-adapter-status',
          adapter: 'auth',
          status: isAppError(error) ? expectedErrorStatus(error) : 'error',
        })
        return resultFromError(error, 'auth_unavailable', 'Sign-in is unavailable right now.')
      }
    },

    async recover(email) {
      if (!isValidEmail(email)) return failure('auth_invalid', 'Enter a valid email address.')
      commit({ type: 'set-adapter-status', adapter: 'auth', status: 'loading' })
      try {
        await adaptersRef.current.auth.recover(email)
        commit({ type: 'set-adapter-status', adapter: 'auth', status: 'ready' })
        commit({ type: 'set-email-next-step', pending: true })
        return success('recovery_ready', 'If that email has an account, recovery steps are ready.')
      } catch (error) {
        commit({
          type: 'set-adapter-status',
          adapter: 'auth',
          status: isAppError(error) ? expectedErrorStatus(error) : 'error',
        })
        return resultFromError(error, 'auth_unavailable', 'Recovery is unavailable right now.')
      }
    },

    completeEmailNextStep() {
      commit({ type: 'set-email-next-step', pending: false })
    },

    logout() {
      savePersistedAppState(storageRef.current, stateRef.current)
      clearPersistedSession(storageRef.current)
      const next = loadPersistedAppState(
        storageRef.current,
        stateRef.current.online,
        clockRef.current.now(),
      )
      commit({ type: 'replace-state', state: next })
    },

    deleteAccount() {
      const userId = stateRef.current.user?.id
      if (!userId) return
      adaptersRef.current.auth.deleteAccount(userId)
      deletePersistedAccount(storageRef.current, userId)
      const next = loadPersistedAppState(
        storageRef.current,
        stateRef.current.online,
        clockRef.current.now(),
      )
      commit({ type: 'replace-state', state: next })
    },

    updateProfile(realName, usernameOrHomeShowId, optionalHomeShowId) {
      const current = stateRef.current
      if (!current.user) return failure('auth_required', 'Sign in to update your profile.')
      const legacyCall = optionalHomeShowId === undefined
      const homeShowId = legacyCall ? usernameOrHomeShowId : optionalHomeShowId
      const name = cleanText(realName)
      const username = legacyCall
        ? profileUsername(current.user)
        : normalizeUsername(usernameOrHomeShowId)
      if (name.length < 2) return failure('validation_error', 'Real name must use at least two characters.')
      if (!isValidUsername(username)) {
        return failure('validation_error', 'Username must use 3–24 letters, numbers, periods, or underscores.')
      }
      if (!current.shows.some((show) => show.id === homeShowId)) {
        return failure('validation_error', 'Choose a show on the current route.')
      }
      const user = {
        ...current.user,
        displayName: name,
        realName: name,
        username,
        homeShowId,
      }
      adaptersRef.current.auth.updateProfile(user)
      commit({ type: 'update-profile', user })
      return success('profile_updated', 'Profile updated.')
    },

    selectShow(showId) {
      if (stateRef.current.shows.some((show) => show.id === showId)) {
        commit({ type: 'select-show', showId })
      }
    },

    toggleSavedShow(showId) {
      if (stateRef.current.shows.some((show) => show.id === showId)) {
        commit({ type: 'toggle-saved-show', showId })
      }
    },

    async connectTicket(rawCode) {
      const current = stateRef.current
      if (!current.user || !current.session) return failure('auth_required', 'Sign in before connecting a ticket.')
      if (!current.online) return failure('ticket_offline', 'Reconnect to check this ticket code.')
      commit({ type: 'set-adapter-status', adapter: 'tickets', status: 'loading' })
      try {
        const result = await adaptersRef.current.tickets.connect(
          rawCode,
          current.user.id,
          current.tickets,
          current.selectedShowId,
        )
        if (!result.ok) {
          const status = result.code === 'ticket_offline'
            ? 'offline'
            : result.code === 'ticket_unavailable' ? 'unavailable' : 'ready'
          commit({ type: 'set-adapter-status', adapter: 'tickets', status })
          return failure(result.code, result.message)
        }
        const show = current.shows.find((candidate) => candidate.id === result.ticket.showId)
        const connectedAt = result.ticket.connectedAt
        commit({
          type: 'ticket-connected',
          ticket: result.ticket,
          itineraryItem: {
            id: `itinerary-${result.ticket.id}`,
            showId: result.ticket.showId,
            kind: 'ticket',
            title: `${show?.city ?? 'Show'} ticket connected`,
            timeLabel: show?.doorsLabel ?? 'SHOW TIME',
            completed: false,
          },
          notification: {
            id: `notification-${result.ticket.id}`,
            kind: 'ticket',
            title: 'Ticket connected',
            body: `${show?.city ?? 'Your show'} is now pinned to your wallet and itinerary.`,
            showId: result.ticket.showId,
            createdAt: connectedAt,
          },
        })
        commit({ type: 'set-adapter-status', adapter: 'tickets', status: 'ready' })
        return success('ticket_connected', 'Ticket connected.')
      } catch (error) {
        commit({ type: 'set-adapter-status', adapter: 'tickets', status: 'error' })
        return resultFromError(error, 'ticket_unavailable', 'Ticket confirmation is temporarily unavailable.')
      }
    },

    unlinkTicket(ticketId) {
      if (!stateRef.current.tickets.some((ticket) => ticket.id === ticketId)) {
        return failure('not_found', 'Ticket was not found.')
      }
      commit({ type: 'ticket-unlinked', ticketId })
      return success('ticket_unlinked', 'Ticket removed from this account.')
    },

    addItineraryItem(showId, title, timeLabel) {
      const current = stateRef.current
      if (!current.user) return failure('auth_required', 'Sign in to build an itinerary.')
      if (!current.shows.some((show) => show.id === showId)) {
        return failure('not_found', 'Show was not found.')
      }
      const cleanTitle = cleanText(title)
      const cleanTime = cleanText(timeLabel)
      if (!cleanTitle || !cleanTime) return failure('validation_error', 'Add both a plan and a time.')
      commit({
        type: 'itinerary-added',
        item: {
          id: idsRef.current.create('plan'),
          showId,
          kind: 'plan',
          title: cleanTitle.slice(0, 100),
          timeLabel: cleanTime.slice(0, 40),
          completed: false,
        },
      })
      return success('itinerary_added', 'Plan added to your itinerary.')
    },

    moveItineraryItem(itemId, direction) {
      const current = stateRef.current
      const itemIndex = current.itinerary.findIndex((item) => item.id === itemId)
      const item = current.itinerary[itemIndex]
      if (!item) return failure('not_found', 'Itinerary item was not found.')
      if (item.kind === 'ticket') {
        return failure('itinerary_item_locked', 'Connected ticket items stay anchored in the timeline.')
      }

      const step = direction === 'earlier' ? -1 : 1
      let targetIndex = itemIndex + step
      while (targetIndex >= 0 && targetIndex < current.itinerary.length) {
        const target = current.itinerary[targetIndex]
        if (!target) break
        if (target.showId === item.showId) {
          if (target.kind === 'ticket') {
            return failure('itinerary_item_locked', 'Connected ticket items stay anchored in the timeline.')
          }
          commit({ type: 'itinerary-moved', itemId, targetItemId: target.id })
          return success(
            direction === 'earlier' ? 'itinerary_moved_earlier' : 'itinerary_moved_later',
            direction === 'earlier' ? 'Plan moved earlier.' : 'Plan moved later.',
          )
        }
        targetIndex += step
      }
      return failure(
        direction === 'earlier' ? 'itinerary_already_first' : 'itinerary_already_last',
        direction === 'earlier' ? 'This plan is already first.' : 'This plan is already last.',
      )
    },

    toggleItineraryItem(itemId) {
      if (stateRef.current.itinerary.some((item) => item.id === itemId)) {
        commit({ type: 'itinerary-toggled', itemId })
      }
    },

    removeItineraryItem(itemId) {
      const item = stateRef.current.itinerary.find((candidate) => candidate.id === itemId)
      if (item?.kind === 'plan' || item?.kind === 'saved') {
        commit({ type: 'itinerary-removed', itemId })
      }
    },

    createPost(showId, channel: FeedChannel, body) {
      const current = stateRef.current
      if (!current.user || !current.session) return failure('auth_required', 'Sign in to post to the city feed.')
      if (!current.shows.some((show) => show.id === showId)) return failure('not_found', 'Show was not found.')
      const cleanBody = cleanText(body)
      if (!cleanBody || cleanBody.length > 500) {
        return failure('validation_error', 'Posts must be between 1 and 500 characters.')
      }
      commit({
        type: 'post-created',
        post: {
          id: idsRef.current.create('post'),
          showId,
          channel,
          authorId: current.user.id,
          authorName: formatUsername(profileUsername(current.user)),
          body: cleanBody,
          createdAt: clockRef.current.now().toISOString(),
          source: 'fan',
        },
      })
      return success('post_created', 'Post added to the city feed.')
    },

    replyToPost(postId, body) {
      const current = stateRef.current
      if (!current.user || !current.session) return failure('auth_required', 'Sign in to reply.')
      const parent = current.feedPosts.find((post) => post.id === postId)
      if (!parent) return failure('not_found', 'Post was not found.')
      const cleanBody = cleanText(body)
      if (!cleanBody || cleanBody.length > 500) {
        return failure('validation_error', 'Replies must be between 1 and 500 characters.')
      }
      commit({
        type: 'post-created',
        post: {
          id: idsRef.current.create('reply'),
          showId: parent.showId,
          channel: parent.channel,
          authorId: current.user.id,
          authorName: formatUsername(profileUsername(current.user)),
          body: cleanBody,
          createdAt: clockRef.current.now().toISOString(),
          parentId: parent.id,
          source: 'fan',
        },
      })
      return success('reply_created', 'Reply added.')
    },

    toggleLike(postId) {
      if (stateRef.current.feedPosts.some((post) => post.id === postId)) {
        commit({ type: 'post-liked', postId })
      }
    },

    toggleSavedPost(postId) {
      if (stateRef.current.feedPosts.some((post) => post.id === postId)) {
        commit({ type: 'post-saved', postId })
      }
    },

    sharePost(postId) {
      if (!stateRef.current.feedPosts.some((post) => post.id === postId)) {
        return failure('not_found', 'Post was not found.')
      }
      commit({ type: 'post-shared', postId })
      return success('post_shared', 'Post link shared.')
    },

    reportPost(postId) {
      if (!stateRef.current.feedPosts.some((post) => post.id === postId)) {
        return failure('not_found', 'Post was not found.')
      }
      if (stateRef.current.reportedPostIds.includes(postId)) {
        return success('post_already_reported', 'You already reported this post.')
      }
      commit({ type: 'post-reported', postId })
      return success('post_reported', 'Report saved for review.')
    },

    undoReportPost(postId) {
      if (!stateRef.current.reportedPostIds.includes(postId)) {
        return failure('post_not_reported', 'This post is not currently reported.')
      }
      commit({ type: 'post-report-undone', postId })
      return success('post_report_undone', 'Report undone.')
    },

    checkIn(showId) {
      const current = stateRef.current
      if (!current.user || !current.session) return failure('auth_required', 'Sign in before checking in.')
      const existing = current.checkIns.find((checkIn) => checkIn.showId === showId)
      if (existing) return success('already_checked_in', 'You are already checked in for this show.')
      if (!current.online) return failure('check_in_offline', 'Reconnect before checking in.')
      const show = current.shows.find((candidate) => candidate.id === showId)
      if (!show) return failure('not_found', 'Show was not found.')
      if (!show.checkInOpen) return failure('check_in_closed', 'Check-in is not open for this show yet.')
      const ticket = current.tickets.find((candidate) => candidate.showId === showId && candidate.status === 'connected')
      if (!ticket) return failure('check_in_ticket_required', 'Connect a ticket for this show before checking in.')
      const completedAt = clockRef.current.now().toISOString()
      const stampId = `stamp-${current.user.id}-${showId}`
      commit({
        type: 'checked-in',
        checkIn: {
          id: `check-in-${current.user.id}-${showId}`,
          showId,
          ticketId: ticket.id,
          completedAt,
          stampId,
        },
        stamp: {
          id: stampId,
          showId,
          earnedAt: completedAt,
          source: 'check-in',
        },
        notification: {
          id: `notification-check-in-${current.user.id}-${showId}`,
          kind: 'passport',
          title: `${show.city} stamp earned`,
          body: 'Your check-in is complete and the stop is now marked in your passport.',
          showId,
          createdAt: completedAt,
        },
      })
      return success('checked_in', 'Check-in complete. Passport stamp earned.')
    },

    markNotificationRead(notificationId) {
      if (stateRef.current.notifications.some((notification) => notification.id === notificationId)) {
        commit({
          type: 'notification-read',
          notificationId,
          readAt: clockRef.current.now().toISOString(),
        })
      }
    },

    markAllNotificationsRead() {
      commit({ type: 'notifications-read-all', readAt: clockRef.current.now().toISOString() })
    },

    updatePreferences(patch: Partial<Preferences>) {
      commit({ type: 'preferences-updated', patch })
    },

    createSupportCase(input) {
      const subject = cleanText(input.subject)
      const message = input.message.trim()
      if (subject.length < 3 || message.length < 10) {
        return failure('validation_error', 'Add a subject and a little more detail.')
      }
      commit({
        type: 'support-created',
        supportCase: {
          id: idsRef.current.create('support'),
          subject: subject.slice(0, 100),
          message: message.slice(0, 2_000),
          createdAt: clockRef.current.now().toISOString(),
          status: 'saved-locally',
        },
      })
      return success('support_saved', 'Private issue note saved on this device. It was not sent to anyone.')
    },

    resetAppData() {
      const current = stateRef.current
      resetPersistedAccountData(storageRef.current, current.user?.id ?? null)
      const next = createInitialAppState(current.online)
      next.user = current.user ? { ...current.user } : null
      next.session = current.session ? { ...current.session } : null
      next.emailNextStepPending = current.emailNextStepPending
      commit({ type: 'replace-state', state: next })
    },

    setOnline(online) {
      adaptersRef.current.setOnline(online)
      commit({ type: 'online-changed', online })
    },
  }), [commit])

  return (
    <AppStateContext.Provider value={state}>
      <AppActionsContext.Provider value={actions}>
        {children}
      </AppActionsContext.Provider>
    </AppStateContext.Provider>
  )
}

export const AppProvider = AppStateProvider

export const useAppState = (): AppState => {
  const value = useContext(AppStateContext)
  if (!value) throw new Error('useAppState must be used inside AppStateProvider.')
  return value
}

export const useAppActions = (): AppActions => {
  const value = useContext(AppActionsContext)
  if (!value) throw new Error('useAppActions must be used inside AppStateProvider.')
  return value
}

export const useAppContext = (): AppContextValue => ({
  state: useAppState(),
  actions: useAppActions(),
})

export default AppStateProvider
